/**
 * GEOLOCATION & SEARCH SERVICE
 * Handles reverse geocoding (Nominatim) and nearby search (Overpass API)
 * Includes robust caching, server rotation, and error handling.
 */

const NOMINATIM_BASE = 'https://nominatim.openstreetmap.org';

// Multi-server rotation for Overpass API to prevent "Search Interrupted" errors
const OVERPASS_ENDPOINTS = [
  'https://overpass-api.de/api/interpreter',
  'https://overpass.kumi.systems/api/interpreter',
  'https://overpass.openstreetmap.fr/api/interpreter'
];

export const APP_VERSION = '1.0.5'; 
const VERSION_KEY = 'nearme_app_version';
const CACHE_TTL_MS = 180 * 24 * 60 * 60 * 1000; // 180 days

export const checkVersionAndClearCache = () => {
  try {
    const savedVersion = localStorage.getItem(VERSION_KEY);
    if (savedVersion !== APP_VERSION) {
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('nearme_')) localStorage.removeItem(key);
      });
      localStorage.setItem(VERSION_KEY, APP_VERSION);
    }
  } catch (e) {
    console.error('Cache purge failed:', e);
  }
};

const cache = {
  get: (key) => {
    try {
      const itemStr = localStorage.getItem(key);
      if (!itemStr) return null;
      const item = JSON.parse(itemStr);
      if (new Date().getTime() > item.expiry) {
        localStorage.removeItem(key);
        return null;
      }
      return item.value;
    } catch (e) { return null; }
  },
  set: (key, value) => {
    try {
      const expiry = new Date().getTime() + CACHE_TTL_MS;
      localStorage.setItem(key, JSON.stringify({ value, expiry }));
    } catch (e) {
      if (e.name === 'QuotaExceededError') {
        Object.keys(localStorage).forEach(k => {
          if (k.startsWith('nearme_')) localStorage.removeItem(k);
        });
      }
    }
  }
};

const getCoordKey = (lat, lng, precision = 4) => `${lat.toFixed(precision)}_${lng.toFixed(precision)}`;
const escapeRegex = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const reverseGeocode = async (lat, lng) => {
  const coordKey = getCoordKey(lat, lng, 4);
  const cacheKey = `nearme_full_addr_${coordKey}`;
  const cached = cache.get(cacheKey);
  if (cached) return cached;

  try {
    const response = await fetch(`${NOMINATIM_BASE}/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`, {
      headers: { 'User-Agent': 'NearMe-PH-App-v1.0.5' }
    });
    const data = await response.json();
    const addr = data.address || {};
    const parts = [
      addr.road || addr.street,
      addr.suburb || addr.neighbourhood,
      addr.city || addr.town || addr.municipality || addr.village
    ].filter(Boolean);

    const result = {
      fullAddress: parts.length > 0 ? parts.join(',') : data.display_name,
      city: addr.city || addr.town || addr.municipality || addr.village || 'Unknown City',
      country: addr.country || 'Philippines',
      countryCode: addr.country_code?.toUpperCase() || 'PH'
    };
    cache.set(cacheKey, result);
    return result;
  } catch (error) {
    return null;
  }
};

export const fetchNearbyPlaces = async (lat, lng, category) => {
  const radius = 2500; // Optimized radius for faster response
  const coordKey = getCoordKey(lat, lng, 3);
  const cacheKey = `nearme_places_${category}_${coordKey}`;
  const cached = cache.get(cacheKey);
  if (cached) return cached;

  const queries = {
    government_public: `nwr["office"~"government|administrative"](around:${radius},${lat},${lng});nwr["amenity"~"townhall|courthouse"](around:${radius},${lat},${lng});`,
    city_hall: `nwr["amenity"="townhall"](around:${radius},${lat},${lng});`,
    police: `nwr["amenity"="police"](around:${radius},${lat},${lng});`,
    fire: `nwr["amenity"="fire_station"](around:${radius},${lat},${lng});`,
    food_essentials: `nwr["amenity"~"restaurant|fast_food|cafe|marketplace"](around:${radius},${lat},${lng});`,
    grocery: `nwr["shop"~"supermarket|grocery|convenience"](around:${radius},${lat},${lng});`,
    bakery: `nwr["shop"="bakery"](around:${radius},${lat},${lng});`,
    restaurants: `nwr["amenity"="restaurant"](around:${radius},${lat},${lng});`,
    health_emergency: `nwr["amenity"~"hospital|pharmacy|clinic|doctors"](around:${radius},${lat},${lng});`,
    hospital: `nwr["amenity"="hospital"](around:${radius},${lat},${lng});`,
    pharmacy: `nwr["amenity"="pharmacy"](around:${radius},${lat},${lng});`,
    clinic: `nwr["amenity"="clinic"](around:${radius},${lat},${lng});`,
    tourist_attractions: `nwr["tourism"~"attraction|museum|viewpoint|zoo|theme_park"](around:${radius},${lat},${lng});nwr["historic"~"monument|memorial|castle"](around:${radius},${lat},${lng});`,
    landmark: `nwr["historic"~"monument|memorial|castle"](around:${radius},${lat},${lng});nwr["tourism"="attraction"](around:${radius},${lat},${lng});`,
    museum: `nwr["tourism"="museum"](around:${radius},${lat},${lng});`,
    park: `nwr["leisure"="park"](around:${radius},${lat},${lng});`,
    viewpoint: `nwr["tourism"="viewpoint"](around:${radius},${lat},${lng});`,
    transport_mobility: `nwr["amenity"~"bus_station|fuel"](around:${radius},${lat},${lng});nwr["highway"="bus_stop"](around:${radius},${lat},${lng});`,
    gas_station: `nwr["amenity"="fuel"](around:${radius},${lat},${lng});`,
    bus_terminal: `nwr["amenity"="bus_station"](around:${radius},${lat},${lng});`,
    finance_bills: `nwr["amenity"~"bank|atm"](around:${radius},${lat},${lng});nwr["shop"="pawnshop"](around:${radius},${lat},${lng});`,
    bank: `nwr["amenity"="bank"](around:${radius},${lat},${lng});`,
    atm: `nwr["amenity"="atm"](around:${radius},${lat},${lng});`,
    lifestyle_personal: `nwr["shop"~"salon|beauty|hairdresser"](around:${radius},${lat},${lng});nwr["leisure"="fitness_centre"](around:${radius},${lat},${lng});`,
    salon: `nwr["shop"~"salon|beauty|hairdresser"](around:${radius},${lat},${lng});`,
    gym: `nwr["leisure"="fitness_centre"](around:${radius},${lat},${lng});`,
    discovery: `nwr["amenity"~"cafe|restaurant|park"](around:1500,${lat},${lng});`,
    cafe: `nwr["amenity"="cafe"](around:${radius},${lat},${lng});`
  };

  const escapedTerm = escapeRegex(category);
  const fallbackQuery = `nwr["name"~"${escapedTerm}",i](around:${radius},${lat},${lng});nwr["amenity"~"${escapedTerm}",i](around:${radius},${lat},${lng});`;
  const searchQuery = queries[category] || fallbackQuery;
  const query = `[out:json][timeout:25];(${searchQuery});out center;`;

  // Failover loop: Try different endpoints if one fails
  let lastError = null;
  for (const endpoint of OVERPASS_ENDPOINTS) {
    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        body: `data=${encodeURIComponent(query)}`,
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      });
      
      if (response.status === 429) continue; // Server busy, try next
      if (!response.ok) continue; // API error, try next
      
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) continue;

      const data = await response.json();
      if (!data || !data.elements) continue;
      
      cache.set(cacheKey, data);
      return data;
    } catch (error) {
      lastError = error;
      continue; // Network error, try next
    }
  }

  throw lastError || new Error('ALL_SERVERS_FAILED');
};